import type {
	Cell as TanStackCell,
	CellContext as TanStackCellContext,
	Column as TanStackColumn,
	ColumnDef as TanStackColumnDef,
	Header as TanStackHeader,
	HeaderContext as TanStackHeaderContext,
	HeaderGroup as TanStackHeaderGroup,
	ReactTable,
	Row as TanStackRow,
	RowData,
	TableOptions as TanStackTableOptions,
} from "@tanstack/react-table";
import {DataTableRoot} from "./DataTableRoot";
import {DataTableContent} from "./DataTableContent";
import {DataTableSortableColumnHeader} from "./DataTableSortableColumnHeader";
import {DataTablePagination} from "./DataTablePagination";
import type {DataTableFeatures} from "./useDataTable";

const DataTable = {
	Root: DataTableRoot,
	Content: DataTableContent,
	SortableColumnHeader: DataTableSortableColumnHeader,
	Pagination: DataTablePagination,
};

export {DataTable};
export {createDataTableColumnHelper, dataTableFeatures, useDataTable} from "./useDataTable";
export {useDataTableContext} from "./DataTableRoot";
export type {DataTableColumnMeta, DataTableFeatures, UseDataTableOptions} from "./useDataTable";
export type {DataTableContentProps} from "./DataTableContent";

/* reexport tanStack to have single source in consumer */
export {flexRender} from "@tanstack/react-table";

/* built-ins outside the registered registries have no string name — pass them as functions per column */
export {
	filterFn_arrHas,
	filterFn_arrIncludesAll,
	filterFn_arrIncludesSome,
	filterFn_between,
	filterFn_betweenInclusive,
	filterFn_empty,
	filterFn_endsWith,
	filterFn_equalsString,
	filterFn_equalsStringSensitive,
	filterFn_greaterThan,
	filterFn_greaterThanOrEqualTo,
	filterFn_includesStringSensitive,
	filterFn_lessThan,
	filterFn_lessThanOrEqualTo,
	filterFn_notEmpty,
	filterFn_startsWith,
	sortFn_alphanumericCaseSensitive,
	sortFn_basic,
	sortFn_textCaseSensitive,
} from "@tanstack/react-table";

export type {
	ColumnFiltersState,
	ColumnVisibilityState,
	OnChangeFn,
	PaginationState,
	RowData,
	RowSelectionState,
	SortingState,
	Updater,
} from "@tanstack/react-table";

/* TanStack types that grew a TFeatures parameter in v9, pre-bound to the data table's feature set */
type Cell<TData extends RowData, TValue = unknown> = TanStackCell<DataTableFeatures, TData, TValue>;
type CellContext<TData extends RowData, TValue = unknown> = TanStackCellContext<DataTableFeatures, TData, TValue>;
type Column<TData extends RowData, TValue = unknown> = TanStackColumn<DataTableFeatures, TData, TValue>;
type ColumnDef<TData extends RowData, TValue = unknown> = TanStackColumnDef<DataTableFeatures, TData, TValue>;
type Header<TData extends RowData, TValue = unknown> = TanStackHeader<DataTableFeatures, TData, TValue>;
type HeaderContext<TData extends RowData, TValue = unknown> = TanStackHeaderContext<DataTableFeatures, TData, TValue>;
type HeaderGroup<TData extends RowData> = TanStackHeaderGroup<DataTableFeatures, TData>;
type Row<TData extends RowData> = TanStackRow<DataTableFeatures, TData>;
type Table<TData extends RowData> = ReactTable<DataTableFeatures, TData>;
type TableOptions<TData extends RowData> = TanStackTableOptions<DataTableFeatures, TData>;

export type {Cell, CellContext, Column, ColumnDef, Header, HeaderContext, HeaderGroup, Row, Table, TableOptions};
